import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { listStaffInvitations, revokeStaffInvitation, type PersonalStaffInvitation } from "@/libs/staffApi";

export function PendingInvitationsPanel({ orgId }: { orgId: string }) {
  const { user } = useAuth();
  if (!user) return null;
  return <PendingInvitationsWorkspace key={JSON.stringify([orgId, user.uid])} orgId={orgId} userId={user.uid} />;
}

function PendingInvitationsWorkspace({ orgId, userId }: { orgId: string; userId: string }) {
  const cache = useQueryClient();
  const key = ["staff-invitations", orgId, userId];
  const query = useQuery({ queryKey: key, queryFn: () => listStaffInvitations(orgId), retry: false });
  const [target, setTarget] = useState<PersonalStaffInvitation | null>(null);
  const command = useRef<string | null>(null);
  const busy = useRef(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = query.data?.filter(item => item.status === "pending") ?? [];
  const revoke = async () => {
    if (!target || busy.current) return;
    busy.current = true;
    setWorking(true);
    setError(null);
    command.current ??= crypto.randomUUID();
    try {
      await revokeStaffInvitation(target.invitation_id, target.revision, command.current);
      setTarget(null);
      command.current = null;
      void cache.invalidateQueries({ queryKey: key });
      void cache.invalidateQueries({ queryKey: ["staff-people", orgId] });
    } catch {
      setError("We couldn’t confirm withdrawal. Retry to check the same request safely.");
    } finally { busy.current = false; setWorking(false); }
  };
  if (query.isError) return <Alert severity="warning" action={<Button onClick={() => void query.refetch()}>Retry</Button>}>Couldn’t load pending invitations.</Alert>;
  if (query.isPending) return <Typography role="status">Loading invitations…</Typography>;
  if (!pending.length) return null;
  return <Paper component="section" variant="outlined" sx={{ p: 2 }}>
    <Stack spacing={2}>
      <Typography component="h2" variant="h6">Pending invitations</Typography>
      {pending.map(item => <Stack key={item.invitation_id} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
        <Stack><Typography sx={{ fontWeight: 650 }}>{item.display_name}</Typography><Typography variant="body2" color="text.secondary">Expires {new Date(item.expires_at).toLocaleDateString()}</Typography></Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={Link} to={`/staff/invitations/${encodeURIComponent(item.invitation_id)}`}>Review</Button>
          <Button disabled={working} onClick={() => { command.current = null; setError(null); setTarget(item); }}>Withdraw</Button>
        </Stack>
      </Stack>)}
    </Stack>
    <Dialog open={Boolean(target)} onClose={() => !busy.current && setTarget(null)}>
      <DialogTitle>Withdraw invitation?</DialogTitle>
      <DialogContent><Stack spacing={2}><Typography>{target?.display_name} will no longer be able to accept this invitation. Their personal account is not changed.</Typography>{error ? <Alert severity="error">{error}</Alert> : null}</Stack></DialogContent>
      <DialogActions><Button disabled={working} onClick={() => setTarget(null)}>Keep invitation</Button><Button disabled={working} color="warning" onClick={() => void revoke()}>{working ? "Withdrawing…" : "Withdraw invitation"}</Button></DialogActions>
    </Dialog>
  </Paper>;
}
